import { BRAND_NAME } from "@cms/config";
import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { Loader2, AlertCircle, CheckCircle2, Save } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@cms/ui/components/card";
import { Button } from "@cms/ui/components/button";
import { Checkbox } from "@cms/ui/components/checkbox";
import { Input } from "@cms/ui/components/input";
import { Label } from "@cms/ui/components/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@cms/ui/components/select";
import { Alert, AlertDescription } from "@cms/ui/components/alert";
import { ColorPicker } from "@cms/ui/components/color-picker";
import { LoadingSpinner } from "@cms/ui/components/loading-spinner";
import { paletteFromTokens, parseDesignTokens } from "@cms/portal-lib/palette";
import type { SocialLink } from "@cms/config";
import { PageHeader } from "@cms/ui/components/page-header";
import { ImageField } from "../components/image-field";
import { SettingsTabs } from "../components/settings-tabs";
import { SocialLinksField, parseSocialLinks, socialLinkError } from "../components/social-links-field";
import type { MediaItem } from "../lib/media";
import type { ClientOutletContext } from "../lib/outlet-context";

// schema.org LocalBusiness subtypes used for the site's JSON-LD markup.
// Mirrors the API's logo_height validation (apps/api/src/tenant.ts).
const LOGO_HEIGHT_RE = /^\d+(\.\d+)?(px|rem|em|%|vh|vw)$/;

// Mirrors the API's header_cta URL rule (apps/api/src/header-cta.ts).
const SAFE_URL_RE = /^(https?:\/\/|mailto:|tel:|\/)/i;
// Sentinel select value for "custom URL" — can't collide with page slugs
// (they're lowercase-only). Same trick as the Navigation page.
const CUSTOM_URL = "__custom_url__";
// Radix Select items can't carry an empty value, so "unset" gets a sentinel.
const NO_LINK = "__none__";

interface HeaderCtaForm {
  type: "disabled" | "button";
  text: string;
  /** A page slug, the CUSTOM_URL sentinel, or "" when unset. */
  link: string;
  url: string;
  /** "" = theme accent. */
  color: string;
}

interface TenantConfigRow {
  business_name?: string | null;
  contact_email?: string | null;
  phone?: string | null;
  address?: string | null;
  schema_type?: string | null;
  logo_r2_key?: string | null;
  logo_height?: string | number | null;
  favicon_r2_key?: string | null;
  footer_logo_r2_key?: string | null;
  footer_logo_height?: string | number | null;
  footer_copyright_text?: string | null;
  social_links_json?: string | null;
  footer_layout?: string | null;
  footer_show_hours?: number | null;
  opening_hours_json?: string | null;
  header_cta?: string | null;
  design_tokens?: string | null;
}

interface PageOption {
  slug: string;
  title: string;
  published?: number;
}

// Parse a stored header_cta JSON string into the form's fields.
function parseHeaderCta(raw: string | null | undefined): HeaderCtaForm {
  const empty: HeaderCtaForm = { type: "disabled", text: "", link: "", url: "", color: "" };
  if (!raw || typeof raw !== "string") return empty;
  try {
    const cta = JSON.parse(raw);
    if (!cta || cta.type !== "button") return empty;
    return {
      type: "button",
      text: cta.text ?? "",
      link: cta.page ?? CUSTOM_URL,
      url: cta.url ?? "",
      color: cta.color ?? "",
    };
  } catch {
    return empty;
  }
}

const SCHEMA_TYPES = [
  { id: "LocalBusiness", label: "General business" },
  { id: "CafeOrCoffeeShop", label: "Café / coffee shop" },
  { id: "Restaurant", label: "Restaurant" },
  { id: "Plumber", label: "Plumber" },
  { id: "Electrician", label: "Electrician" },
  { id: "HomeAndConstructionBusiness", label: "Builder / construction" },
  { id: "HairSalon", label: "Hair salon / barber" },
  { id: "MedicalClinic", label: "Clinic / healthcare" },
  { id: "ProfessionalService", label: "Professional services" },
];

export function SettingsPage() {
  const { api, refreshStatus, setBreadcrumbs } = useOutletContext<ClientOutletContext>();

  useEffect(() => {
    setBreadcrumbs?.([{ label: "Settings" }]);
    return () => setBreadcrumbs?.(null);
  }, [setBreadcrumbs]);

  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const [businessName, setBusinessName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [schemaType, setSchemaType] = useState("LocalBusiness");
  const [logoKey, setLogoKey] = useState<string | null>(null);
  // Transient preview for a logo picked this session; stored keys have none.
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  // Optional CSS length for the header logo, e.g. "48px", "3rem", "10%"
  // ("" = use the theme default).
  const [logoHeight, setLogoHeight] = useState("");
  const [faviconKey, setFaviconKey] = useState<string | null>(null);
  // Transient preview for a favicon picked this session; stored keys have none.
  const [faviconUrl, setFaviconUrl] = useState<string | null>(null);
  const [footerLogoKey, setFooterLogoKey] = useState<string | null>(null);
  // Transient preview for a footer logo picked this session.
  const [footerLogoUrl, setFooterLogoUrl] = useState<string | null>(null);
  // Optional CSS length for the footer logo ("" = use the theme default).
  const [footerLogoHeight, setFooterLogoHeight] = useState("");
  // Copyright line override; "" = the default "© <year> <business name>."
  const [footerCopyright, setFooterCopyright] = useState("");
  // Footer v2: social icon links, layout variant and opening-hours toggle.
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([]);
  const [footerLayout, setFooterLayout] = useState<"columns" | "centered">("columns");
  const [footerShowHours, setFooterShowHours] = useState(false);
  // Header call-to-action; type "disabled" | "button". link is a page slug or
  // the CUSTOM_URL sentinel; color "" = theme accent.
  const [cta, setCta] = useState<HeaderCtaForm>(parseHeaderCta(null));
  const [pages, setPages] = useState<PageOption[]>([]);
  // Raw design_tokens JSON — feeds the colour picker's palette presets.
  const [designTokens, setDesignTokens] = useState<string | null>(null);

  useEffect(() => {
    if (!api) return;
    Promise.all([
      api.request<{ config?: TenantConfigRow | null }>("/tenant/config"),
      api.request<{ pages?: PageOption[] }>("/tenant/pages"),
    ])
      .then(([data, pagesData]) => {
        const config = data.config ?? {};
        setBusinessName(config.business_name ?? "");
        setContactEmail(config.contact_email ?? "");
        setPhone(config.phone ?? "");
        setAddress(config.address ?? "");
        setSchemaType(config.schema_type ?? "LocalBusiness");
        setLogoKey(config.logo_r2_key ?? null);
        setLogoHeight(config.logo_height != null ? String(config.logo_height) : "");
        setFaviconKey(config.favicon_r2_key ?? null);
        setFooterLogoKey(config.footer_logo_r2_key ?? null);
        setFooterLogoHeight(config.footer_logo_height != null ? String(config.footer_logo_height) : "");
        setFooterCopyright(config.footer_copyright_text ?? "");
        setSocialLinks(parseSocialLinks(config.social_links_json));
        setFooterLayout(config.footer_layout === "centered" ? "centered" : "columns");
        setFooterShowHours(Boolean(config.footer_show_hours));
        setCta(parseHeaderCta(config.header_cta));
        setPages(pagesData.pages ?? []);
        setDesignTokens(config.design_tokens ?? null);
        setLoaded(true);
      })
      .catch((err: Error) => setLoadError(err.message));
  }, [api]);

  const logoHeightError = logoHeight.trim() && !LOGO_HEIGHT_RE.test(logoHeight.trim())
    ? 'Use a CSS length like "48px", "3rem" or "10%".'
    : "";

  const footerLogoHeightError = footerLogoHeight.trim() && !LOGO_HEIGHT_RE.test(footerLogoHeight.trim())
    ? 'Use a CSS length like "48px", "3rem" or "10%".'
    : "";

  const socialLinksInvalid = socialLinks.some((link) => socialLinkError(link));

  const ctaError = cta.type !== "button"
    ? ""
    : !cta.text.trim()
      ? "Enter the button text."
      : !cta.link
        ? "Choose where the button links to."
        : cta.link === CUSTOM_URL && !SAFE_URL_RE.test(cta.url.trim())
          ? "The URL must start with https://, http://, mailto:, tel: or /."
          : "";

  const setCtaField = (field: keyof HeaderCtaForm, value: string) =>
    setCta((prev) => ({ ...prev, [field]: value }));

  // Tenant palette (design tokens) → colour-picker presets, as on the canvas.
  const palettePresets = paletteFromTokens(parseDesignTokens(designTokens)).map((value) => ({ value }));

  const changeLogo = (item: MediaItem | null) => {
    setLogoKey(item?.key ?? null);
    setLogoUrl(item?.url ?? null);
    if (item) {
      setNotice({ ok: true, text: "Logo chosen — remember to save settings to apply it." });
    }
  };

  const changeFavicon = (item: MediaItem | null) => {
    setFaviconKey(item?.key ?? null);
    setFaviconUrl(item?.url ?? null);
    if (item) {
      setNotice({ ok: true, text: "Favicon chosen — remember to save settings to apply it." });
    }
  };

  const changeFooterLogo = (item: MediaItem | null) => {
    setFooterLogoKey(item?.key ?? null);
    setFooterLogoUrl(item?.url ?? null);
    if (item) {
      setNotice({ ok: true, text: "Footer logo chosen — remember to save settings to apply it." });
    }
  };

  const save = async () => {
    if (!api) return;
    setSaving(true);
    setNotice(null);
    try {
      await api.request("/tenant/config", {
        method: "PUT",
        body: JSON.stringify({
          business_name: businessName || null,
          contact_email: contactEmail || null,
          phone: phone || null,
          address: address || null,
          schema_type: schemaType,
          logo_r2_key: logoKey,
          logo_height: logoHeight.trim() || null,
          favicon_r2_key: faviconKey,
          footer_logo_r2_key: footerLogoKey,
          footer_logo_height: footerLogoHeight.trim() || null,
          footer_copyright_text: footerCopyright.trim() || null,
          social_links_json: socialLinks
            .map((link) => ({ ...link, url: link.url.trim() }))
            .filter((link) => link.url),
          footer_layout: footerLayout === "centered" ? "centered" : null,
          footer_show_hours: footerShowHours,
          header_cta: cta.type === "button"
            ? {
                type: "button",
                text: cta.text.trim(),
                ...(cta.link === CUSTOM_URL ? { url: cta.url.trim() } : { page: cta.link }),
                ...(cta.color ? { color: cta.color } : {}),
              }
            : null,
        }),
      });
      setNotice({
        ok: true,
        text: "Settings saved. Publish the site from the top bar to make changes go live.",
      });
      // Saving stages a change — refresh so the header's "Unpublished changes"
      // badge and Publish button appear right away.
      refreshStatus?.();
    } catch (err) {
      setNotice({ ok: false, text: err instanceof Error ? err.message : String(err) });
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{loadError}</AlertDescription>
      </Alert>
    );
  }

  if (!loaded) {
    return <LoadingSpinner className="py-20" />;
  }

  return (
    <div>
      <SettingsTabs
        action={
          <Button size="sm" onClick={save} disabled={saving || !!logoHeightError || !!footerLogoHeightError || !!ctaError || socialLinksInvalid}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save settings
          </Button>
        }
      />

      <PageHeader title="General" />

      {notice && (
        <Alert variant={notice.ok ? "default" : "destructive"} className="mb-4">
          {notice.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          <AlertDescription>{notice.text}</AlertDescription>
        </Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Business details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="business-name">Business name</Label>
              <Input id="business-name" value={businessName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBusinessName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-email">Contact email</Label>
              <Input id="contact-email" type="email" value={contactEmail} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setContactEmail(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" value={phone} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhone(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="address">Address</Label>
              <Input id="address" value={address} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddress(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="schema-type">Business category</Label>
              <Select value={schemaType} onValueChange={setSchemaType}>
                <SelectTrigger id="schema-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SCHEMA_TYPES.map((option) => (
                    <SelectItem key={option.id} value={option.id}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Helps search engines and AI assistants understand what kind of business you are.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Logo</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <ImageField
              api={api}
              value={logoKey ? { key: logoKey, url: logoUrl } : null}
              onChange={changeLogo}
              emptyLabel="No logo yet"
              chooseLabel="Choose logo"
              replaceLabel="Replace logo"
            />
            <div className="space-y-1.5">
              <Label htmlFor="logo-height">Logo height</Label>
              <Input
                id="logo-height"
                type="text"
                placeholder="Theme default"
                value={logoHeight}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLogoHeight(e.target.value)}
                className="w-40"
                aria-invalid={logoHeightError ? "true" : undefined}
              />
              {logoHeightError ? (
                <p className="text-xs text-destructive">{logoHeightError}</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  How tall the logo appears in the header, e.g. "48px", "3rem", "10%". Leave
                  blank to use the theme default. SVG and PNG/JPG logos are supported.
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Favicon</CardTitle>
            <CardDescription>
              The small icon shown in browser tabs and bookmarks. A square PNG or SVG works best.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ImageField
              api={api}
              value={faviconKey ? { key: faviconKey, url: faviconUrl } : null}
              onChange={changeFavicon}
              emptyLabel="No favicon yet"
              chooseLabel="Choose favicon"
              replaceLabel="Replace favicon"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Footer</CardTitle>
            <CardDescription>
              An optional logo and the copyright line at the bottom of every page.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <ImageField
              api={api}
              value={footerLogoKey ? { key: footerLogoKey, url: footerLogoUrl } : null}
              onChange={changeFooterLogo}
              emptyLabel="No footer logo yet"
              chooseLabel="Choose footer logo"
              replaceLabel="Replace footer logo"
            />
            <div className="space-y-1.5">
              <Label htmlFor="footer-logo-height">Footer logo height</Label>
              <Input
                id="footer-logo-height"
                type="text"
                placeholder="Theme default"
                value={footerLogoHeight}
                onChange={(e) => setFooterLogoHeight(e.target.value)}
                className="w-40"
                aria-invalid={footerLogoHeightError ? "true" : undefined}
              />
              {footerLogoHeightError ? (
                <p className="text-xs text-destructive">{footerLogoHeightError}</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  How tall the logo appears in the footer, e.g. "48px", "3rem". Leave blank
                  to use the theme default.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="footer-copyright">Copyright text</Label>
              <Input
                id="footer-copyright"
                value={footerCopyright}
                onChange={(e) => setFooterCopyright(e.target.value)}
                placeholder={`© ${new Date().getFullYear()} ${businessName || "Your business"}.`}
                maxLength={200}
              />
              <p className="text-xs text-muted-foreground">
                Leave blank to use the default. "Website by {BRAND_NAME}." is always
                shown after it.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="footer-layout">Layout</Label>
              <Select
                value={footerLayout}
                onValueChange={(value: string) => setFooterLayout(value === "centered" ? "centered" : "columns")}
              >
                <SelectTrigger id="footer-layout">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="columns">Columns</SelectItem>
                  <SelectItem value="centered">Centered</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Centered stacks the logo, social icons, contact details and hours down the middle.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="footer-show-hours"
                checked={footerShowHours}
                onChange={(e) => setFooterShowHours(e.target.checked)}
              />
              <Label htmlFor="footer-show-hours">Show opening hours in the footer</Label>
            </div>
            <SocialLinksField value={socialLinks} onChange={setSocialLinks} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Header call to action</CardTitle>
            <CardDescription>
              An optional button on the right of your site's header — the navigation moves to the centre.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="cta-type">Call to action</Label>
              <Select value={cta.type} onValueChange={(value: string) => setCtaField("type", value)}>
                <SelectTrigger id="cta-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="disabled">Disabled</SelectItem>
                  <SelectItem value="button">Button</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {cta.type === "button" && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="cta-text">Button text</Label>
                  <Input
                    id="cta-text"
                    value={cta.text}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCtaField("text", e.target.value)}
                    placeholder="Book now"
                    maxLength={60}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cta-link">Links to</Label>
                  <Select
                    value={cta.link || NO_LINK}
                    onValueChange={(value: string) => setCtaField("link", value === NO_LINK ? "" : value)}
                  >
                    <SelectTrigger id="cta-link">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_LINK}>Choose a page…</SelectItem>
                      {pages.map((page) => (
                        <SelectItem key={page.slug} value={page.slug}>
                          {page.title} {page.published ? "" : "(draft)"}
                        </SelectItem>
                      ))}
                      {cta.link && cta.link !== CUSTOM_URL && !pages.some((p) => p.slug === cta.link) && (
                        <SelectItem value={cta.link}>/{cta.link} (page not found)</SelectItem>
                      )}
                      <SelectItem value={CUSTOM_URL}>Custom URL…</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {cta.link === CUSTOM_URL && (
                  <div className="space-y-1.5">
                    <Label htmlFor="cta-url">Custom URL</Label>
                    <Input
                      id="cta-url"
                      value={cta.url}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCtaField("url", e.target.value)}
                      placeholder="https://…"
                    />
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label>Button colour</Label>
                  <div className="flex items-center gap-2">
                    <ColorPicker
                      value={cta.color}
                      onChange={(value: string) => setCtaField("color", value)}
                      presets={palettePresets}
                      ariaLabel="Button colour"
                    />
                    {cta.color ? (
                      <Button variant="ghost" size="sm" onClick={() => setCtaField("color", "")}>
                        Use theme colour
                      </Button>
                    ) : (
                      <span className="text-xs text-muted-foreground">Theme colour (pick to override)</span>
                    )}
                  </div>
                </div>
                {ctaError && <p className="text-xs text-destructive">{ctaError}</p>}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
